import {
  Box,
  Divider,
  Grid,
  Stack,
  Switch,
  Typography,
  TypographyPropsVariantOverrides,
} from "@mui/material";
import { TypographyVariant } from "@mui/material/styles/createTypography";
import { OverridableStringUnion } from "@mui/types";
import { isNil } from "lodash";
import { useEffect, useState } from "react";
import {
  Control,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from "react-hook-form";

import { colors } from "../../../styles/colors";
import fontWeight from "../../../styles/mui/fontWeight";
import {
  Body,
  Header,
  Title,
  ClearDetails,
  ClearDetailsButton,
  VerticalBar,
} from "../styles/OrderingPhysician";
import PhysicianInputForm from "../containers/PhysicianInputForm";

import {
  OrderingPhysicianDetails,
  PrimaryPhysicianDetails,
} from "../../../models/PhysicianDetails";
import {
  physicianNames,
  primaryPhysicianFormNames,
} from "../../../constants/PhysicianDetails";
import { flexAlignCentre } from "../../../styles/mui/styles/display";
import { TabData } from "../../../models/TabSessionStorage";
import {
  getFormattedPhoneNo,
  getFormValues,
  getValue,
  isEmptyValue,
} from "../../../utils";
import { fieldNames } from "../../../constants/AllPatientRecord";

export interface PropsFromState {
  getValues: UseFormGetValues<TabData>;
  control: Control<TabData, any>;
  clearErrors: UseFormClearErrors<TabData>;
  watch: UseFormWatch<TabData>;
  reset: UseFormReset<TabData>;
  setValue: UseFormSetValue<TabData>;
  unregister: UseFormUnregister<any>;
}

type AllProps = PropsFromState;

const OrderingPhysician: React.FC<AllProps> = ({
  getValues,
  setValue,
  reset,
  watch,
  control,
  unregister,
  clearErrors,
}: AllProps) => {
  const [orderingPhysician, setOrderingPhysician] =
    useState<OrderingPhysicianDetails>();
  const [primaryPhysician, setPrimaryPhysician] =
    useState<PrimaryPhysicianDetails>();

  const [
    openPrimaryPhysicianAutocomplete,
    setOpenPrimaryPhysicianAutocomplete,
  ] = useState(false);
  const [openOrderingPhysician, setOpenOrderingPhysician] = useState(false);
  const [openPrimaryPhysician, setOpenPrimaryPhysician] = useState(false);
  const [
    openOrderingPhysicianAutocomplete,
    setOpenOrderingPhysicianAutocomplete,
  ] = useState(false);
  const [toggleState, setToggleState] = useState(false);
  const [noValidate, setNoValidate] = useState<boolean>(true);

  useEffect(() => {
    setToggleState(getValues("primaryPhysician.sameAsOrdering") || false);
  }, []);

  useEffect(() => {
    const subscription = watch((value: any, { name }: any) => {
      if (value && name && value.orderingPhysician) {
        handlePrimaryPhysicianState(name, value, setValue, toggleState);
      }
      if (!isEmptyValue(getFormValues(fieldNames, getValues))) {
        setNoValidate(false);
      } else {
        setNoValidate(true);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, toggleState]);

  const physicianFieldMap: {
    ordering: string;
    primary: string;
    transform?: (root: any) => any;
  }[] = [
    { ordering: "orderingPhysician.physicianName", primary: "primaryPhysician.physicianName" },
    { ordering: "orderingPhysician.physicianNpiId", primary: "primaryPhysician.physicianNpiId" },
    { ordering: "orderingPhysician.fax", primary: "primaryPhysician.fax" },
    { ordering: "orderingPhysician.phoneNo", primary: "primaryPhysician.phoneNo" },
    { ordering: "orderingPhysician.phoneExt", primary: "primaryPhysician.phoneExt" },
    { ordering: "orderingPhysician.streetName1", primary: "primaryPhysician.streetName1" },
    { ordering: "orderingPhysician.streetName2", primary: "primaryPhysician.streetName2" },
    { ordering: "orderingPhysician.county", primary: "primaryPhysician.county" },
    { ordering: "orderingPhysician.city", primary: "primaryPhysician.city" },
    { ordering: "orderingPhysician.physicianState", primary: "primaryPhysician.physicianState" },
    { ordering: "orderingPhysician.zipCode", primary: "primaryPhysician.zipCode" },
    {
      ordering: "orderingPhysician.latitude",
      primary: "primaryPhysician.latitude",
      transform: (root) => getValue(root, "orderingPhysician.latitude", ""),
    },
    {
      ordering: "orderingPhysician.longitude",
      primary: "primaryPhysician.longitude",
      transform: (root) => getValue(root, "orderingPhysician.longitude", ""),
    },
  ];

  const handlePrimaryPhysicianState = (
    name: string | undefined,
    value: any,
    setValue: UseFormSetValue<TabData>,
    isOrderingPhysician: boolean
  ) => {
    if (!isOrderingPhysician || !name || !value?.orderingPhysician) return;

    const matched = physicianFieldMap.find(
      (f) => f.ordering === name && !isNil(getValue(value, f.ordering))
    );
    if (!matched) return;

    const rawVal = matched.transform
      ? matched.transform(value)
      : getValue(value, matched.ordering, "");

    // Normalize undefined/null (except numeric lat/long which may be null)
    const finalVal =
      rawVal === null || rawVal === undefined ? "" : rawVal;

    setValue(matched.primary as any, finalVal);
  };

  const choosePrimaryPhysician = (option: any) => {
    setPrimaryPhysician(option);
    if (!isNil(option)) {
      const {
        physicianName,
        physicianNpiId,
        city,
        physicianState,
        zipCode,
        streetName1,
        streetName2,
        fax,
        county,
        phoneExt,
        phoneNo,
      } = option;
      reset({
        ...getValues(),
        primaryPhysician: {
          ...getValues("primaryPhysician"),
          physicianName: physicianName,
          physicianNpiId: physicianNpiId,
          streetName1: streetName1,
          streetName2: streetName2,
          city: city,
          county: county,
          physicianState: physicianState,
          zipCode: zipCode,
          fax: getFormattedPhoneNo(fax),
          phoneExt: phoneExt,
          phoneNo: phoneNo ? getFormattedPhoneNo(phoneNo) : "",
          physicianTypeId: 1,
          sameAsOrdering: false,
        },
      });
    }
  };

  const chooseOrderingPhysician = (option: OrderingPhysicianDetails) => {
    setOrderingPhysician(option);
    if (!isNil(option)) {
      const {
        physicianName,
        physicianNpiId,
        city,
        physicianState,
        zipCode,
        streetName1,
        streetName2,
        fax,
        county,
        phoneExt,
        phoneNo,
      } = option;
      reset({
        ...getValues(),
        orderingPhysician: {
          ...getValues("orderingPhysician"),
          physicianName: physicianName,
          physicianNpiId: physicianNpiId,
          streetName1: streetName1,
          streetName2: streetName2,
          city: city,
          county: county,
          zipCode: zipCode,
          fax: getFormattedPhoneNo(fax),
          physicianState: physicianState,
          phoneExt: phoneExt,
          phoneNo: phoneNo ? getFormattedPhoneNo(phoneNo) : "",
          physicianTypeId: 2,
        },
      });
      if (toggleState) {
        reset({
          ...getValues(),
          primaryPhysician: {
            ...getValues("orderingPhysician"),
            physicianTypeId: 1,
            sameAsOrdering: true,
          },
        });
      }
    }
  };

  const typographyBox = (
    variant:
      | OverridableStringUnion<
          "inherit" | TypographyVariant,
          TypographyPropsVariantOverrides
        >
      | undefined,
    value: string,
    color: string,
    fontWeight: string,
    sx?: object
  ) => {
    return (
      <Typography
        variant={variant}
        fontWeight={fontWeight}
        color={color}
        sx={sx}
      >
        {value}
      </Typography>
    );
  };

  const orderingPhysicianFormNames = {
    physicianName: "orderingPhysician.physicianName",
    physicianNpiId: "orderingPhysician.physicianNpiId",
    phoneNo: "orderingPhysician.phoneNo",
    phoneExt: "orderingPhysician.phoneExt",
    taxId: "orderingPhysician.taxId",
    address: [
      "orderingPhysician.streetName1",
      "orderingPhysician.streetName2",
      "orderingPhysician.city",
      "orderingPhysician.county",
      "orderingPhysician.physicianState",
      "orderingPhysician.zipCode",
      "orderingPhysician.latitude",
      "orderingPhysician.longitude",
    ],
    fax: "orderingPhysician.fax",
  };

  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setToggleState(isChecked);
      reset({
        ...getValues(),
        primaryPhysician: {
          ...getValues("orderingPhysician"),
          physicianTypeId: 1,
          sameAsOrdering: true,
        },
      });
    } else {
      clearPrimaryPhysicianDetails();
    }
  };

  const clearPrimaryPhysicianDetails = () => {
    setToggleState(false);
    reset({
      ...getValues(),
      primaryPhysician: {
        physicianName: "",
        physicianNpiId: "",
        streetName1: "",
        streetName2: "",
        city: "",
        county: "",
        physicianState: "",
        zipCode: "",
        fax: "",
        phoneExt: "",
        phoneNo: "",
        physicianTypeId: 1,
        sameAsOrdering: false,
        longitude: null,
        latitude: null,
      },
    });
    setNoValidate(true);
  };

  const clearOrderingPhysicianDetails = () => {
    if (toggleState) {
      clearPrimaryPhysicianDetails();
      setToggleState(false);
    }
    reset({
      ...getValues(),
      orderingPhysician: {
        physicianName: "",
        physicianNpiId: "",
        streetName1: "",
        streetName2: "",
        city: "",
        county: "",
        physicianState: "",
        zipCode: "",
        fax: "",
        phoneExt: "",
        phoneNo: "",
        physicianTypeId: 2,
        longitude: null,
        latitude: null,
      },
      primaryPhysician: {
        ...getValues("primaryPhysician"),
        sameAsOrdering: false,
      },
    });
  };

  useEffect(() => {
    if (noValidate) {
      unregister("primaryPhysician");
      clearErrors("primaryPhysician");
    }
  }, [noValidate])

  const renderClearButton = (
    onClick: React.MouseEventHandler<HTMLDivElement>
  ) => (
    <Box sx={flexAlignCentre} width={"100%"}>
      <Box sx={ClearDetailsButton} onClick={onClick}>
        {typographyBox(
          "subtitle2",
          `Clear Details`,
          colors.fonts[4],
          fontWeight.Weight[5],
          ClearDetails
        )}
      </Box>
    </Box>
  );

  return (
    <>
      {/* Ordering Physician */}
      <Grid size={{ xs: 12 }} pt={"0.5rem"}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Grid container sx={Header}>
              <Grid size={{ xs: 6 }}>
                <Box sx={Title}>
                  {typographyBox(
                    "h6",
                    "Ordering Physician",
                    colors.fonts[20],
                    fontWeight.Weight[5]
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 6 }}>
                {renderClearButton(clearOrderingPhysicianDetails)}
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} sx={Body} data-testid="ordering-physician">
            <PhysicianInputForm
              // useForm hook methods
              control={control}
              getValues={getValues}
              setValue={setValue}
              reset={reset}
              // assign selected primary state and form names
              physician={orderingPhysician || null}
              names={orderingPhysicianFormNames}
              // assign handlers
              choosePhysician={chooseOrderingPhysician}
              open={openOrderingPhysician}
              setOpen={setOpenOrderingPhysician}
              openAutocomplete={openOrderingPhysicianAutocomplete}
              setOpenAutocomplete={setOpenOrderingPhysicianAutocomplete}
              physicianFormName={physicianNames.ordering}
              noValidate={false}
            />
          </Grid>
        </Grid>
      </Grid>
      {/* Primary Physician */}
      <Grid size={{ xs: 12 }} pt={"1rem"}>
        <Grid container>
          <Grid size={{ xs: 12 }}>
            <Grid container sx={Header}>
              <Grid size={{ xs: 6 }}>
                <Box sx={Title} display={"flex"}>
                  {typographyBox(
                    "h6",
                    `Primary Care Physician (Optional)`,
                    colors.fonts[20],
                    fontWeight.Weight[5]
                  )}
                  {typographyBox(
                    "h6",
                    "|",
                    colors.fonts[20],
                    fontWeight.Weight[5],
                    VerticalBar
                  )}
                  <Stack direction="row" spacing={3} alignItems="center">
                    <Switch
                      inputProps={{ "aria-label": "ant design" }}
                      onChange={handleToggle}
                      checked={toggleState}
                      sx={{ marginRight: "-0.8rem !important" }}
                    />
                    {typographyBox(
                      "subtitle2",
                      "Same as Ordering physician",
                      colors.fonts[4],
                      fontWeight.Weight[6]
                    )}
                  </Stack>
                </Box>
              </Grid>
              <Grid size={{ xs: 6 }}>
                {renderClearButton(clearPrimaryPhysicianDetails)}
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }} sx={Body} data-testid="primary-physician">
            <PhysicianInputForm
              // useForm hook methods
              control={control}
              getValues={getValues}
              setValue={setValue}
              reset={reset}
              // assign selected state and form names
              physician={primaryPhysician || null}
              names={primaryPhysicianFormNames}
              // assign handlers
              choosePhysician={choosePrimaryPhysician}
              open={openPrimaryPhysician}
              setOpen={setOpenPrimaryPhysician}
              setOpenAutocomplete={setOpenPrimaryPhysicianAutocomplete}
              openAutocomplete={openPrimaryPhysicianAutocomplete}
              physicianFormName={physicianNames.primary}
              isReadOnly={toggleState}
              noValidate={noValidate}
            />
          </Grid>
        </Grid>
        <Divider />
      </Grid>
    </>
  );
};

export default OrderingPhysician;
